import { createClient } from '@supabase/supabase-js'
import SecurityUtils from './security-utils'

// Security: Setup console filtering IMMEDIATELY before any other code
const isProduction = process.env.NODE_ENV === 'production' || process.env.VUE_APP_ENVIRONMENT === 'production'

// Apply security filters in both development and production for URL protection
const originalLog = console.log
const originalWarn = console.warn
const originalError = console.error
const originalDebug = console.debug

const sensitivePatterns = [
  /access_token/i,
  /refresh_token/i,
  /bearer/i,
  /jwt/i,
  /password/i,
  /secret/i,
  /key.*[:=]/i,
  /token.*[:=]/i,
  /auth.*token/i,
  /GoTrueClient/i,
  /session from storage/i,
  /eyJ[A-Za-z0-9-_=]+\./i, // JWT pattern
  /https:\/\/[a-z0-9]+\.supabase\.co/i, // Supabase database URLs
  /supabase\.co\/rest\/v1/i, // Supabase REST API endpoints
  /user_profiles\?select=/i, // Database table queries
  /404 \(Not Found\)/i, // API error responses that might expose URLs
  /403 \(Forbidden\)/i, // Auth API permission errors
  /auth\/v1\/admin\/users.*403/i, // Admin API forbidden errors
  /window\.fetch @ supabase\.js/i, // Browser DevTools stack traces
  /GET https:\/\/[a-z0-9]+\.supabase\.co/i // Network request logs
]

const filterSensitive = (...args) => {
  const message = args.join(' ')
  if (sensitivePatterns.some(pattern => pattern.test(message))) {
    // In development, show a filtered message instead of hiding completely
    if (!isProduction) {
      return ['🔒 [FILTERED] Database request (URLs hidden for security)']
    }
    // In production, hide completely
    return []
  }
  return args
}

console.log = (...args) => {
  const filtered = filterSensitive(...args)
  if (filtered.length > 0) {
    originalLog.apply(console, filtered)
  }
}

console.warn = (...args) => {
  const filtered = filterSensitive(...args)
  if (filtered.length > 0) {
    originalWarn.apply(console, filtered)
  }
}

console.error = (...args) => {
  const filtered = filterSensitive(...args)
  if (filtered.length > 0) {
    originalError.apply(console, filtered)
  }
}

console.debug = (...args) => {
  const filtered = filterSensitive(...args)
  if (filtered.length > 0) {
    originalDebug.apply(console, filtered)
  }
}

// Supabase configuration
const supabaseUrl = 'https://yowdzdkzsrdoptrlvtic.supabase.co'
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inlvd2R6ZGt6c3Jkb3B0cmx2dGljIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTIxOTEyMTQsImV4cCI6MjA2Nzc2NzIxNH0.t9XujnS7aUfwCu1rq3LCWmu8ZsH0ED8LbJC08IBeYlU'

// Use the already defined isProduction variable
const debugMode = !isProduction && (process.env.VUE_APP_SUPABASE_DEBUG !== 'false')

// Supabase client options with security configurations
const supabaseOptions = {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Set session to last for 7 days
    storageKey: 'sb-yowdzdkzsrdoptrlvtic-auth-token',
    storage: window.localStorage,
    debug: debugMode // Enable debug mode only in development
  },
  global: {
    headers: {
      'apikey': supabaseKey,
      'Content-Type': 'application/json',
      'X-Client-Info': 'interview-signal'
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseKey, supabaseOptions)

// Session management constants
const SESSION_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
const SESSION_KEY = 'interview_signal_session';

// Session helpers
export const sessionHelpers = {
  createSession(user) {
    const session = {
      user,
      createdAt: Date.now(),
      expiresAt: Date.now() + SESSION_DURATION,
      lastActivity: Date.now()
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    return session;
  },

  getSession() {
    const sessionData = localStorage.getItem(SESSION_KEY);
    if (!sessionData) return null;

    try {
      const session = JSON.parse(sessionData);
      const now = Date.now();

      // Check if session has expired
      if (now > session.expiresAt) {
        this.clearSession();
        return null;
      }

      // Update last activity if session is still active
      session.lastActivity = now;
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      
      return session;
    } catch (error) {
      console.error('Error parsing session:', error);
      this.clearSession();
      return null;
    }
  },

  updateSession(user) {
    const session = this.getSession();
    if (session) {
      session.user = user;
      session.lastActivity = Date.now();
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    }
  },

  clearSession() {
    console.log('🧹 Clearing session data...')
    
    // Clear session-related localStorage items
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('currentUser'); // Legacy cleanup
    
    // Clear all Supabase auth tokens - updated with correct key
    const supabaseKeys = [
      'sb-auth-token',
      'sb-yowdzdkzsrdoptrlvtic-auth-token',
      'supabase.auth.token',
      `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token` // Dynamic key based on project
    ]
    
    supabaseKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key)
        console.log(`✅ Cleared: ${key}`)
      }
    })
    
    // Clear any keys that start with 'sb-' (Supabase related)
    const allKeys = Object.keys(localStorage)
    allKeys.forEach(key => {
      if (key.startsWith('sb-') && !supabaseKeys.includes(key)) {
        localStorage.removeItem(key)
        console.log(`✅ Cleared Supabase key: ${key}`)
      }
    })
    
    console.log('✅ Session data cleared')
  },

  isSessionValid() {
    const session = this.getSession();
    return session !== null;
  },

  extendSession() {
    const session = this.getSession();
    if (session) {
      session.expiresAt = Date.now() + SESSION_DURATION;
      session.lastActivity = Date.now();
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    }
  }
};

// Auth helpers
export const authHelpers = {
  // Test Supabase connection
  async testConnection() {
    console.log('🧪 Testing Supabase connection from browser...')
    
    try {
      // Test basic fetch to Supabase
      const response = await fetch(supabaseUrl + '/rest/v1/', {
        headers: {
          'apikey': supabaseKey,
          'Authorization': `Bearer ${supabaseKey}`
        }
      })
      
      console.log('🌐 Fetch test:', {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText
      })
      
      // Test auth endpoint
      const { data, error } = await supabase.auth.getSession()
      console.log('🔐 Auth test:', { hasData: !!data, error: error?.message })
      
      return {
        fetchOk: response.ok,
        fetchStatus: response.status,
        authOk: !error,
        authError: error?.message
      }
    } catch (error) {
      console.error('❌ Connection test failed:', error)
      return {
        fetchOk: false,
        fetchStatus: 0,
        authOk: false,
        authError: error.message
      }
    }
  },

  async signUp(email, password, userData = {}) {
    console.log('🔐 Supabase SignUp called with:', { email, userData })
    
    if (!supabase) {
      console.error('❌ Supabase client not available')
      return { data: null, error: { message: 'Authentication service not available' } }
    }

    try {
      console.log('🚀 Attempting Supabase signup...')
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            ...userData,
            status: 'approved', // Auto-approve new users
            full_name: userData.fullName || userData.full_name || ''
          }
        }
      })
      
      console.log('📡 Supabase signup response:', { 
        hasUser: !!data?.user,
        userEmail: data?.user?.email,
        hasSession: !!data?.session,
        errorMessage: error?.message,
        errorDetails: error
      })
      
      if (error) {
        console.error('❌ Signup error details:', {
          message: error.message,
          status: error.status,
          name: error.name
        })
        
        // Handle specific error cases
        if (error.message?.includes('User already registered')) {
          return { data: null, error: { message: 'An account with this email already exists. Please sign in instead.' }}
        }
        
        if (error.message?.includes('Password should be at least')) {
          return { data: null, error: { message: 'Password must be at least 6 characters long.' }}
        }
        
        if (error.message?.includes('fetch')) {
          return { data: null, error: { 
            message: 'Network connection failed. Please check your internet connection and try again.',
            originalError: error
          }}
        }
        
        return { data: null, error }
      }
      
      if (!data?.user) {
        console.error('❌ No user returned from Supabase signup')
        return { data: null, error: { message: 'Signup failed. No user data received.' }}
      }
      
      // Create extended session for successful signup
      console.log('✅ Signup successful, creating session...')
      sessionHelpers.createSession(data.user);
      console.log('✅ Session created successfully')
      
      // Automatically create Stripe customer after successful signup
      console.log('💳 Triggering automatic customer creation...')
      try {
        await this.createCustomerAfterSignup(data.user)
      } catch (customerError) {
        console.warn('⚠️ Customer creation will be handled by background service:', customerError.message)
      }
      
      return { data, error: null }
      
    } catch (networkError) {
      console.error('🚨 Network/Connection Error during signup:', {
        message: networkError.message,
        name: networkError.name,
        stack: networkError.stack
      })
      
      return { 
        data: null, 
        error: { 
          message: 'Connection failed during signup. Please check your internet connection and try again.',
          originalError: networkError.message
        } 
      }
    }
  },

  async signIn(email, password) {
    console.log('🔐 Attempting to sign in with:', email)
    console.log('🌐 Supabase URL:', supabaseUrl)
    console.log('🔑 Using API key (first 20 chars):', supabaseKey.substring(0, 20) + '...')
    
    if (!supabase) {
      console.error('❌ Supabase client not available')
      return { data: null, error: { message: 'Authentication service not available' } }
    }

    try {
      // Test basic connectivity first
      console.log('🧪 Testing Supabase connection...')
      const { data: healthCheck, error: healthError } = await supabase.auth.getSession()
      console.log('🏥 Health check result:', { healthCheck: !!healthCheck, healthError })

      console.log('🚀 Attempting sign in...')
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })
      
      console.log('📡 Supabase signIn raw response:', { 
        hasUser: !!data?.user,
        userEmail: data?.user?.email,
        hasSession: !!data?.session,
        errorMessage: error?.message,
        errorDetails: error
      })
      
      if (error) {
        console.error('❌ Sign in error details:', {
          message: error.message,
          status: error.status,
          statusCode: error.status,
          name: error.name,
          cause: error.cause
        })
        
        // Handle specific error cases
        if (error.message?.includes('fetch')) {
          return { data: null, error: { 
            message: 'Network connection failed. Please check your internet connection and try again.',
            originalError: error
          }}
        }
        
        if (error.message?.includes('Invalid login credentials')) {
          return { data: null, error: { message: 'Invalid email or password. Please check your credentials.' }}
        }
        
        if (error.message?.includes('Email not confirmed')) {
          return { data: null, error: { message: 'Please verify your email address before signing in.' }}
        }
        
        if (error.message?.includes('Too many requests')) {
          return { data: null, error: { message: 'Too many login attempts. Please wait a moment and try again.' }}
        }
        
        return { data: null, error }
      }
      
      if (!data?.user) {
        console.error('❌ No user returned from Supabase')
        return { data: null, error: { message: 'Login failed. No user data received.' }}
      }
      
      // Create extended session for successful login
      console.log('✅ Sign in successful, creating session...')
      sessionHelpers.createSession(data.user);
      console.log('✅ Session created successfully')
      
      return { data, error: null }
      
    } catch (networkError) {
      console.error('🚨 Network/Connection Error:', {
        message: networkError.message,
        name: networkError.name,
        stack: networkError.stack,
        cause: networkError.cause
      })
      
      return { 
        data: null, 
        error: { 
          message: 'Connection failed. Please check your internet connection and try again.',
          originalError: networkError.message
        } 
      }
    }
  },

  async signOut() {
    console.log('🚪 Signing out user...')
    
    if (!supabase) {
      console.error('❌ Supabase client not available')
      sessionHelpers.clearSession();
      return { error: null }
    }

    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.error('❌ Supabase signOut error:', error)
      } else {
        console.log('✅ Supabase signOut successful')
      }
      
      // Always clear session data regardless of Supabase response
      sessionHelpers.clearSession();
      
      console.log('✅ SignOut complete')
      return { error: null }
      
    } catch (signOutError) {
      console.error('🚨 SignOut Error:', signOutError)
      
      // Still clear local session even if Supabase signout fails
      sessionHelpers.clearSession();
      
      return { error: null } // Don't expose signout errors to user
    }
  },

  async getCurrentUser() {
    if (!supabase) {
      console.error('❌ Supabase client not available for getCurrentUser')
      return null
    }

    try {
      const { data: { user }, error } = await supabase.auth.getUser()
      
      if (error) {
        console.error('❌ Error getting current user:', error)
        return null
      }
      
      return user
    } catch (getUserError) {
      console.error('🚨 Get User Error:', getUserError)
      return null
    }
  },

  async updateProfile(updates) {
    if (!supabase) {
      console.error('❌ Supabase client not available for updateProfile')
      return { data: null, error: { message: 'Authentication service not available' } }
    }

    try {
      console.log('🔄 Updating user profile with:', updates)
      
      // Update user metadata
      const { data, error } = await supabase.auth.updateUser({
        data: updates
      })
      
      if (error) {
        console.error('❌ Profile update error:', error)
        return { data: null, error }
      }
      
      // Also update the user_customization table if profile info is being updated
      if (data?.user) {
        try {
          // Check if this is profile information that should be synced to user_customization
          if (updates.full_name || updates.firstName || updates.lastName || updates.email || updates.phone) {
            const profileData = {
              user_id: data.user.id,
              personal_info: {
                firstName: updates.firstName || updates.full_name?.split(' ')[0] || '',
                lastName: updates.lastName || updates.full_name?.split(' ').slice(1).join(' ') || '',
                email: updates.email || data.user.email,
                phone: updates.phone || ''
              }
            }

            console.log('🔄 Syncing profile to user_customization table...')
            // Upsert to user_customization table
            const { error: profileError } = await supabase
              .from('user_customization')
              .upsert(profileData, { 
                onConflict: 'user_id',
                ignoreDuplicates: false 
              })

            if (profileError) {
              console.log('⚠️ Profile sync failed (non-critical):', profileError)
              // Don't fail the entire update for this
            } else {
              console.log('✅ Profile synced to database')
            }
          }
        } catch (syncError) {
          console.log('⚠️ Profile sync error (non-critical):', syncError)
          // Don't fail the entire update for this
        }
      }
      
      console.log('✅ Profile update successful')
      return { data, error: null }
      
    } catch (updateError) {
      console.error('🚨 Profile Update Error:', updateError)
      return { 
        data: null, 
        error: { 
          message: 'Failed to update profile. Please try again.',
          originalError: updateError.message
        } 
      }
    }
  },

  isAdmin(user) {
    console.warn('authHelpers.isAdmin is deprecated. Use AdminService.isAdmin instead.')
    return user?.email === 'nisjet.lapi@gmail.com'
  },

  // Check if Supabase is configured and available
  isConfigured() {
    const isConfigured = !!(supabase && supabaseUrl && supabaseKey)
    // Only log in development mode and without sensitive details
    if (process.env.NODE_ENV === 'development') {
      console.debug('Supabase configuration status:', isConfigured ? 'Ready' : 'Not configured')
    }
    return isConfigured
  },

  // Automatically create customer record after successful signup
  async createCustomerAfterSignup(user) {
    console.log('🔄 Auto-creating Stripe customer for signup:', user.email)
    
    // Check if customer was already created during signup (e.g., payment-first signup)
    if (user.user_metadata?.stripe_customer_id) {
      console.log('✅ Stripe customer already exists from signup flow:', user.user_metadata.stripe_customer_id)
      console.log('⏭️ Skipping duplicate customer creation')
      return
    }
    
    try {
      // Create Stripe customer directly during signup
      const customerResponse = await fetch('http://localhost:3001/api/stripe/create-customer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: user.email,
          name: user.user_metadata?.full_name || user.email
        })
      })
      
      if (!customerResponse.ok) {
        throw new Error(`Customer creation failed: ${await customerResponse.text()}`)
      }
      
      const customerData = await customerResponse.json()
      console.log('✅ Stripe customer created:', customerData.id)
      
      // Create subscription record in Supabase
      const trialEndDate = new Date()
      trialEndDate.setDate(trialEndDate.getDate() + 3) // 3-day trial
      
      // Import supabase with service role for this operation
      const { createClient } = await import('@supabase/supabase-js')
      const supabaseServiceRole = createClient(
        'https://yowdzdkzsrdoptrlvtic.supabase.co',
        'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inlvd2R6ZGt6c3Jkb3B0cmx2dGljIiwicm9sZSI6InNlcnZpY2Vfcm9sZSIsImlhdCI6MTc1MjE5MTIxNCwiZXhwIjoyMDY3NzY3MjE0fQ.wJZnlKoJhFJqFJ0Bg1f4e_caMZSq4gZfKy6XWYpQZWo'
      )
      
      const { error: insertError } = await supabaseServiceRole
        .from('user_subscriptions')
        .insert({
          user_id: user.id,
          user_email: user.email,
          stripe_customer_id: customerData.id,
          stripe_subscription_id: `sub_signup_${Date.now()}`,
          stripe_price_id: 'price_signup_trial',
          status: 'trialing',
          plan_name: 'Premium Trial (3 days)',
          trial_end: trialEndDate.toISOString(),
          current_period_start: new Date().toISOString(),
          current_period_end: trialEndDate.toISOString(),
          cancel_at_period_end: false
        })
      
      if (insertError) {
        console.error('❌ Database insert failed:', insertError.message)
        // Don't fail signup, but log the issue
      } else {
        console.log('✅ Subscription record created with 3-day trial')
      }
      
    } catch (error) {
      console.warn('⚠️ Auto-customer creation failed during signup:', error.message)
      // Don't fail the signup if customer creation fails
    }
  }
}

// Enhanced network request sanitization
const originalFetch = window.fetch
window.fetch = async function(url, options) {
  const urlString = typeof url === 'string' ? url : url?.toString() || ''
  const isSensitive = sensitivePatterns.some(pattern => pattern.test(urlString))
  
  try {
    const result = await originalFetch.call(this, url, options)
    
    // For sensitive URLs, don't let the browser log them
    if (isSensitive && !result.ok) {
      // Create a sanitized error that doesn't expose the URL
      console.debug('🔒 [FILTERED] API request failed:', result.status, result.statusText)
    }
    
    return result
  } catch (error) {
    if (isSensitive) {
      console.debug('🔒 [FILTERED] Network request failed')
      throw new Error(`API request failed: ${error.status || 'Network error'}`)
    }
    throw error
  }
}

// Additional console filtering for any remaining URL exposures
const originalWindowFetch = window.fetch
Object.defineProperty(window, 'fetch', {
  value: function(url, options) {
    const urlString = typeof url === 'string' ? url : url?.toString() || ''
    if (sensitivePatterns.some(pattern => pattern.test(urlString))) {
      // For sensitive URLs, return a wrapper that prevents console logging
      return originalWindowFetch.call(this, url, options).catch(error => {
        console.debug('🔒 [FILTERED] Protected API request error')
        throw error
      })
    }
    return originalWindowFetch.call(this, url, options)
  },
  configurable: true,
  writable: true
})